import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { useToast } from '../lib/toast'
import { Thumb } from './Thumb'
import { addWeeks, formatWeekRange, isSameWeek, mondayOf, relativeWeekLabel } from '../lib/dates'
import { CHECK_THE_PACKET, conflictLine, conflictOf, worthAsking, type Conflict } from '../lib/conflicts'
import {
  conflictLevel,
  DAY_NAMES,
  type Bootstrap,
  type ProfileAllergen,
  type ShoppingExtra,
  type ShoppingLine,
  type ShoppingState,
  type Week,
} from '@shared/types'

interface Props {
  boot: Bootstrap
  week: Week | null
  weekStart: string
  /** Same week state as the board: shopping for another week moves the board too. */
  onNavigate: (startDate: string) => void
  /** Offer the home-screen install here: this is the list you want in a shop. */
  showInstall: boolean
  onInstall: () => void
  onDismissInstall: () => void
  onError: (err: unknown, fallback: string) => void
}

export function ShoppingPage({
  boot,
  week,
  weekStart,
  onNavigate,
  showInstall,
  onInstall,
  onDismissInstall,
  onError,
}: Props) {
  const toast = useToast()
  const [lines, setLines] = useState<ShoppingLine[] | null>(null)
  /* The hand-written half, keyed on the Monday rather than this week row. */
  const [extras, setExtras] = useState<ShoppingExtra[]>([])
  const [lastWeekHas, setLastWeekHas] = useState(0)
  /* Each child's allergens, so a food is checked against whoever it is for. */
  const [allergensByKid, setAllergensByKid] = useState<Record<string, ProfileAllergen[]>>({})
  const [adding, setAdding] = useState('')
  const [busy, setBusy] = useState(false)

  const weekId = week?.id ?? null

  const load = useCallback(async () => {
    try {
      /*
       * Asked by the Monday, not by this child's week: there is one shop, so
       * the list is every child's lunches folded together. It also means a
       * Monday this child has no board for still shows the siblings' food and
       * anything someone has written down.
       */
      const list = await api.shoppingList(weekStart)
      setLines(list.lines)
      setExtras(list.extras)
      setLastWeekHas(list.extrasLastWeek)
      setAllergensByKid(list.allergens)
    } catch (err) {
      onError(err, 'Could not build the shopping list.')
    }
    // weekId: a board built or rebuilt on the other tab changes the list.
  }, [weekId, weekStart, onError])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * Marks are stored on the server rather than per device: two people shop
   * from this list, so ticking something off has to change it for both.
   */
  const mark = useCallback(
    async (itemId: string, state: ShoppingState | null) => {
      const before = lines
      // Optimistic: a shop is a rapid sequence of taps and should feel instant.
      setLines((prev) => prev?.map((l) => (l.itemId === itemId ? { ...l, state } : l)) ?? prev)
      try {
        await api.setShoppingMark(weekStart, itemId, state)
      } catch (err) {
        setLines(before ?? null)
        onError(err, 'Could not update the list.')
      }
    },
    [weekStart, lines, onError],
  )

  /*
   * Adding, renaming and removing take the server's answer: they change which
   * rows exist, and every route replies with the whole list, which is what
   * the page renders anyway.
   */
  const withExtras = useCallback(
    async (run: () => Promise<ShoppingExtra[]>, fallback: string) => {
      if (busy) return
      setBusy(true)
      try {
        setExtras(await run())
      } catch (err) {
        onError(err, fallback)
      } finally {
        setBusy(false)
      }
    },
    [busy, onError],
  )

  /*
   * Ticking is the exception, and matches `mark` above: a shop is a rapid
   * sequence of taps, and a tick that waits for a round trip feels broken
   * standing in an aisle. One field on a row already on screen, so it moves
   * first and goes back if the write fails.
   */
  const markExtra = useCallback(
    async (id: string, state: ShoppingState | null) => {
      const before = extras
      setExtras((prev) => prev.map((e) => (e.id === id ? { ...e, state } : e)))
      try {
        setExtras(await api.setShoppingExtra(id, { state }))
      } catch (err) {
        setExtras(before)
        onError(err, 'Could not update the list.')
      }
    },
    [extras, onError],
  )

  const addExtra = async () => {
    const name = adding.trim()
    if (!name) return
    setAdding('')
    await withExtras(
      () => api.addShoppingExtra(weekStart, name),
      'Could not add that to the list.',
    )
  }

  const copyLastWeek = async () => {
    await withExtras(async () => {
      const { extras: next, copied } = await api.copyShoppingExtras(weekStart, addWeeks(weekStart, -1))
      toast.notify(copied === 1 ? 'Brought 1 extra forward.' : `Brought ${copied} extras forward.`)
      return next
    }, 'Could not copy last week’s extras.')
  }

  /* Everyone the list is for, in family order: the server sorts each line's kids that way. */
  const kidsOnList = useMemo(() => {
    const seen = new Map<string, string>()
    for (const line of lines ?? []) for (const k of line.kids) if (!seen.has(k.profileId)) seen.set(k.profileId, k.name)
    return [...seen.values()]
  }, [lines])

  const { toBuy, have } = useMemo(() => {
    const all = lines ?? []
    return {
      toBuy: all.filter((l) => l.state !== 'have'),
      have: all.filter((l) => l.state === 'have'),
    }
  }, [lines])

  /* Same split for the hand-written half: "have it" means the same thing
     whether the line came from a lunch or from someone's head. */
  const extrasToBuy = useMemo(() => extras.filter((e) => e.state !== 'have'), [extras])
  const extrasHave = useMemo(() => extras.filter((e) => e.state === 'have'), [extras])

  const grouped = useMemo(
    () =>
      boot.categories
        .map((category) => ({ category, lines: toBuy.filter((l) => l.categoryId === category.id) }))
        .filter((g) => g.lines.length > 0),
    [toBuy, boot.categories],
  )

  /*
   * The shop is where the label actually gets read, so this is the placement
   * that matters most. The server's list carries no allergens, but it does not
   * need to: the pantry in `boot` already has every tag, keyed by the same
   * item id, so the match is a lookup rather than a round trip.
   */
  const flags = useMemo(() => {
    const byId = new Map(boot.items.map((i) => [i.id, i]))
    const out = new Map<string, Flag>()
    for (const line of lines ?? []) {
      /*
       * Checked against each child the food is for, not the one whose board
       * is open: Bo's peanut butter is still Bo's problem from Ada's tab.
       * Several children's rows fold into one conflict for the level, and
       * the sentence names each child once there is more than one.
       */
      const hits = line.kids
        .map((k) => ({ kid: k, c: conflictOf(byId.get(line.itemId), allergensByKid[k.profileId] ?? []) }))
        .filter((h): h is { kid: ShoppingLine['kids'][number]; c: Conflict } => h.c !== null)
      if (hits.length === 0) continue
      const rows = hits.flatMap((h) => h.c.rows)
      const text = hits
        .map(({ kid, c }) => {
          const said = conflictLine(c, kid.name)
          return kidsOnList.length > 1 && !said.includes(kid.name) ? `${kid.name}: ${said}` : said
        })
        .join(' · ')
      out.set(line.itemId, { level: conflictLevel(rows), rows, text })
    }
    return out
  }, [lines, boot.items, allergensByKid, kidsOnList])

  /*
   * The banner carries the loud ones only -- a severe allergy or a school
   * rule. Every quiet 'avoid' still gets its own line further down, but a
   * summary listing seven cheeses because someone mildly avoids milk is a
   * summary that gets skipped, and the peanut line goes with it.
   *
   * Only what is still being bought, too: a thing you already have is not a
   * shopping decision.
   */
  const toCheck = useMemo(
    () => toBuy.filter((l) => worthAsking(flags.get(l.itemId) ?? null)),
    [toBuy, flags],
  )

  async function copyList() {
    if (!lines) return

    const text = grouped
      .map(({ category, lines: rows }) =>
        [
          `${category.name}:`,
          ...rows
            .filter((r) => r.state !== 'bought')
            .map((r) => {
              const who = kidsOnList.length > 1 ? forWhom(r) : ''
              const qty = r.count > 1 ? `x${r.count}` : ''
              const note = [qty, who].filter(Boolean).join(', ')
              return `  - ${r.name}${note ? ` (${note})` : ''}`
            }),
        ].join('\n'),
      )
      .filter((block) => block.includes('\n'))
      .join('\n\n')
    // The extras go in as their own block, exactly as they read on screen.
    const alsoRows = extrasToBuy.filter((e) => e.state !== 'bought')
    const also = alsoRows.length > 0 ? `\n\nAlso getting:\n${alsoRows.map((e) => `  - ${e.name}`).join('\n')}` : ''
    try {
      await navigator.clipboard.writeText(
        `Lunch shopping — ${formatWeekRange(weekStart)}\n\n${text}${also}`,
      )
      toast.notify('Shopping list copied.')
    } catch {
      toast.warn('Could not copy — your browser blocked clipboard access.')
    }
  }

  /* Everything still to buy, both halves: the number in the header is what
     is left in the shop, not what is left of the derived list. */
  const remaining =
    toBuy.filter((l) => l.state !== 'bought').length +
    extrasToBuy.filter((e) => e.state !== 'bought').length
  const haveCount = have.length + extrasHave.length
  /* Anything at all to show, so a week with no lunches still renders a list
     someone has written by hand. */
  const anything = (lines?.length ?? 0) > 0 || extras.length > 0

  /*
   * The one banner on this page, and only on a phone. Standing in a shop with
   * the list open is the moment an installed app is obviously better than a
   * browser tab, and it is the only moment worth saying so.
   */
  const installBar = showInstall ? (
    <div className="install-bar">
      <span>
        📲 Add this to your home screen and the list opens in one tap, signal or not.
      </span>
      <button type="button" className="linklike" onClick={onInstall}>
        Show me how
      </button>
      <button type="button" className="x" aria-label="Not now" onClick={onDismissInstall}>
        ✕
      </button>
    </div>
  ) : null

  const weekNav = (
    <div className="week-bar">
      <button className="btn icon" onClick={() => onNavigate(addWeeks(weekStart, -1))} aria-label="Previous week">
        ←
      </button>
      <div className="week-title">
        <strong>Shopping</strong>
        <span>
          {formatWeekRange(weekStart)} · {relativeWeekLabel(weekStart)}
          {anything && ` · ${remaining} to buy${haveCount > 0 ? ` · ${haveCount} already have` : ''}`}
        </span>
      </div>
      <button className="btn icon" onClick={() => onNavigate(addWeeks(weekStart, 1))} aria-label="Next week">
        →
      </button>
      {!isSameWeek(weekStart) && (
        <button className="btn sm" onClick={() => onNavigate(mondayOf())}>
          This week
        </button>
      )}
      <div className="spacer" />
      {anything && (
        <button className="btn" onClick={copyList}>
          Copy list
        </button>
      )}
    </div>
  )

  /*
   * A group of its own, after the categories and never mixed into them. A
   * hand-written "kitchen roll" filed under FRUIT is how a clear list turns
   * to mush, and the whole point of this half is that the page is carryable.
   */
  const alsoGetting = (
    <div className="shop-group also-group">
      <div className="cat-head" style={{ cursor: 'default' }}>
        📝 Also getting
        {extrasToBuy.length > 0 && <span className="count">{extrasToBuy.length}</span>}
      </div>

      {extrasToBuy.map((extra) => {
        const bought = extra.state === 'bought'
        return (
          <div className={`shop-line extra ${bought ? 'done' : ''}`} key={extra.id}>
            <input
              type="checkbox"
              checked={bought}
              onChange={() => void markExtra(extra.id, bought ? null : 'bought')}
              aria-label={`${extra.name} — ${bought ? 'in the trolley' : 'still to buy'}`}
            />
            <span className="name">
              <span className="shop-name">{extra.name}</span>
            </span>
            <button
              className="btn ghost sm"
              onClick={() => void markExtra(extra.id, 'have')}
              title="Already in the cupboard — do not buy"
            >
              Have it
            </button>
            <button
              className="x"
              aria-label={`Take ${extra.name} off the list`}
              title="Take it off the list"
              onClick={() =>
                void withExtras(() => api.removeShoppingExtra(extra.id), 'Could not remove that.')
              }
            >
              ✕
            </button>
          </div>
        )
      })}

      <form
        className="extra-add"
        onSubmit={(e) => {
          e.preventDefault()
          void addExtra()
        }}
      >
        <input
          type="text"
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          placeholder="Milk, kitchen roll…"
          aria-label="Something else to buy"
          maxLength={60}
        />
        <button className="btn sm" type="submit" disabled={busy || adding.trim() === ''}>
          Add
        </button>
      </form>

      {/*
        * Only when there is nothing here and something there. Most of this
        * list repeats weekly, and retyping it is how a list like this stops
        * being used -- the same answer the board gives with Copy last week.
        */}
      {extras.length === 0 && lastWeekHas > 0 && (
        <button className="btn sm extra-copy" onClick={() => void copyLastWeek()} disabled={busy}>
          Copy last week’s {lastWeekHas === 1 ? 'extra' : `${lastWeekHas} extras`}
        </button>
      )}
    </div>
  )

  /*
   * No week at all. This used to return before the week bar was even built,
   * which left the tab with no way out: a family whose current week is
   * unplanned could not reach the week that is.
   */
  if (lines && lines.length === 0 && !week) {
    return (
      <>
        {weekNav}
        {installBar}
        <div className="empty">
          <span className="big" aria-hidden="true">
            🛒
          </span>
          Plan the week of {formatWeekRange(weekStart)} first, then the shopping list builds itself.
        </div>
        {/* Nothing is derived without a board, but milk is still milk. */}
        {alsoGetting}
      </>
    )
  }

  if (!lines) {
    return (
      <>
        {weekNav}
        {installBar}
        <p className="muted">Building your list…</p>
      </>
    )
  }

  if (lines.length === 0) {
    return (
      <>
        {weekNav}
        {installBar}
        <div className="empty">
          <span className="big" aria-hidden="true">
            🛒
          </span>
          Nothing packed for the week of {formatWeekRange(weekStart)} yet.
        </div>
        {/* The derived half is empty, which says nothing about the other one. */}
        {alsoGetting}
      </>
    )
  }

  const row = (line: ShoppingLine) => {
    const bought = line.state === 'bought'
    const flag = flags.get(line.itemId)
    return (
      <div
        className={`shop-line ${bought ? 'done' : ''} ${flag ? `flag-${flag.level}` : ''}`}
        key={line.itemId}
      >
        <input
          type="checkbox"
          checked={bought}
          onChange={() => void mark(line.itemId, bought ? null : 'bought')}
          aria-label={`${line.name} — ${bought ? 'in the trolley' : 'still to buy'}`}
        />
        <Thumb emoji={line.emoji} imageKey={line.imageKey} name={line.name} />
        <span className="name">
          <span className="shop-name">
            {line.name}
            {line.count > 1 && <span className="muted"> ×{line.count}</span>}
          </span>
          {/*
            * Who it is for, once the list is for more than one child. The
            * total says how many lunches; this says how many lunchboxes,
            * which is the part that decides one cucumber or three.
            */}
          {kidsOnList.length > 1 && <span className="shop-kids muted small">{forWhom(line)}</span>}
          {flag && (
            <span className="shop-flag">
              <span aria-hidden="true">{flag.level === 'severe' ? '⚠️' : flag.level === 'rule' ? '🚫' : '•'}</span>{' '}
              {flag.text}
            </span>
          )}
        </span>
        <span className="days">
          {line.days
            .slice()
            .sort((a, b) => a - b)
            .map((d) => DAY_NAMES[d - 1]!.slice(0, 3))
            .join(', ')}
        </span>
        <button
          className="btn ghost sm"
          onClick={() => void mark(line.itemId, 'have')}
          title="Already in the cupboard — do not buy"
        >
          Have it
        </button>
      </div>
    )
  }

  return (
    <>
      {weekNav}
      {installBar}

      {/*
        * Above the list, not buried in it. Someone scanning a trolley reads
        * the top of the screen and the line they are standing on, and the
        * whole point of this placement is that the packet is in their hand.
        */}
      {toCheck.length > 0 && (
        <div className="shop-check" role="note">
          <strong>
            {toCheck.length} {toCheck.length === 1 ? 'thing' : 'things'} to check on the shelf
          </strong>
          <ul>
            {toCheck.map((l) => (
              <li key={l.itemId}>
                <b>{l.name}</b>: {flags.get(l.itemId)!.text}
              </li>
            ))}
          </ul>
          <p className="muted small">{CHECK_THE_PACKET}</p>
        </div>
      )}

      {kidsOnList.length > 1 && (
        <p className="muted small shop-for">One list for {listOf(kidsOnList)}.</p>
      )}

      {grouped.map(({ category, lines: rows }) => (
        <div className="shop-group" key={category.id}>
          <div className="cat-head" style={{ cursor: 'default' }}>
            <span className="slot-dot" style={{ background: category.color }} aria-hidden="true" />
            {category.name}
          </div>
          {rows.map(row)}
        </div>
      ))}

      {alsoGetting}

      {haveCount > 0 && (
        <div className="shop-group have-group">
          <div className="cat-head" style={{ cursor: 'default' }}>
            🏠 Already have — not buying
            <span className="count">{haveCount}</span>
          </div>
          {have.map((line) => (
            <div className="shop-line have" key={line.itemId}>
              <Thumb emoji={line.emoji} imageKey={line.imageKey} name={line.name} />
              <span className="name">{line.name}</span>
              <button className="btn ghost sm" onClick={() => void mark(line.itemId, null)}>
                Need it after all
              </button>
            </div>
          ))}
          {/* Both halves land in the same group: what you have, you have. */}
          {extrasHave.map((extra) => (
            <div className="shop-line have extra" key={extra.id}>
              <span className="name">{extra.name}</span>
              <button
                className="btn ghost sm"
                onClick={() => void markExtra(extra.id, null)}
              >
                Need it after all
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

/** A conflict with its sentence already written for the children it is about. */
type Flag = Conflict & { text: string }

/** "Ada", "Ada and Bo", "Ada, Bo and Cass". */
function listOf(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** "3 kids · Ada ×3, Bo ×2, Cass" -- or just "Bo ×2" when it is one child's. */
function forWhom(line: ShoppingLine): string {
  const each = line.kids.map((k) => (k.count > 1 ? `${k.name} ×${k.count}` : k.name)).join(', ')
  return line.kids.length > 1 ? `${line.kids.length} kids · ${each}` : each
}
